import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errDetail } from "@agent/lib/api";

/** Loads a draft by id and owns all draft-editing mutations (regenerate, blog version, save). */
export function useDraftEditor(id, navigate) {
  const [draft, setDraft] = useState(null);
  const [instruction, setInstruction] = useState("");
  const [regenLoading, setRegenLoading] = useState(false);
  const [blogGenLoading, setBlogGenLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showBlog, setShowBlog] = useState(false);

  useEffect(() => {
    if (id) api.get(`/drafts/${id}`).then((r) => setDraft(r.data)).catch(() => { toast.error("Draft not found"); navigate("/agent"); });
    else setDraft(null);
  }, [id, navigate]);

  const regenerate = async () => {
    setRegenLoading(true);
    try {
      const { data } = await api.post(`/drafts/${draft.id}/regenerate`, { instruction });
      setDraft(data);
      setInstruction("");
      toast.success("Draft regenerated — previous version saved");
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setRegenLoading(false);
    }
  };

  const generateBlog = async () => {
    setBlogGenLoading(true);
    try {
      const { data } = await api.post(`/drafts/${draft.id}/generate-blog`, { instruction: "" });
      setDraft(data);
      setShowBlog(true);
      toast.success("Blog version generated — review it below");
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setBlogGenLoading(false);
    }
  };

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const payload = { title: draft.title, content: draft.content, ...extra };
      if (draft.blog_markdown != null) {
        payload.blog_title = draft.blog_title || "";
        payload.blog_markdown = draft.blog_markdown;
        payload.blog_excerpt = draft.blog_excerpt || "";
        payload.blog_tags = draft.blog_tags || [];
      }
      const { data } = await api.put(`/drafts/${draft.id}`, payload);
      setDraft(data);
      toast.success(extra.status === "approved" ? "Draft approved" : "Saved");
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setSaving(false);
    }
  };

  return {
    draft, setDraft, instruction, setInstruction, regenLoading, regenerate,
    blogGenLoading, generateBlog, saving, save, showBlog, setShowBlog,
  };
}
